import { useCallback, useEffect, useRef, useState } from 'react';
import { apiRequest } from '../services/apiClient';

/**
 * Generic data-fetching hook.
 * Eliminates the repeated loading/error/data state pattern across 20+ components.
 *
 * @param {string|null} url - API path to fetch. Pass null to skip fetching.
 * @param {object} [options]
 * @param {Array}  [options.deps=[]] - Extra dependencies that trigger a refetch.
 * @param {*}      [options.initialData=null] - Value before first successful fetch.
 * @param {object} [options.requestOptions] - Extra options forwarded to apiRequest (e.g. timeoutMs).
 * @returns {{ data, isLoading, error, refetch }}
 */
export function useFetch(url, { deps = [], initialData = null, requestOptions } = {}) {
  const [data, setData] = useState(initialData);
  const [isLoading, setIsLoading] = useState(Boolean(url));
  const [error, setError] = useState('');
  const abortRef = useRef(null);

  const fetch = useCallback(async () => {
    if (!url) return;

    if (abortRef.current) {
      abortRef.current.abort();
    }

    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);
    setError('');

    try {
      const result = await apiRequest(url, { ...(requestOptions || {}), signal: controller.signal });
      if (!controller.signal.aborted) {
        setData(result);
      }
    } catch (err) {
      if (!controller.signal.aborted) {
        setError(err.message || 'Unable to load data. Check your connection and try again.');
      }
    } finally {
      if (!controller.signal.aborted) {
        setIsLoading(false);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, requestOptions, ...deps]);

  useEffect(() => {
    fetch();
    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, [fetch]);

  return { data, isLoading, error, refetch: fetch };
}
